import React from "react";
import { ClientSettings, OnClientSave } from "@design-system";
import { createClient, createClientVariables, FindFilteredClients } from "../../../graphql/types";
import { UpdateClientMutation, UpdateClientMutationVariables } from "../../../gql/graphql";
import { CREATE_CLIENT, UPDATE_CLIENT } from "../../../graphql/mutation";
import { clientPresentInSearch, useFilteredClientsLazy } from "../../../hooks/clients";
import { FILTERED_CLIENTS_QUERY } from "../../../graphql/query";
import { useMutation } from "../../../hooks";

export const ClientSettingsContainer = () => {
  const clientFilterData = useFilteredClientsLazy(10);
  const { loading } = clientFilterData;

  const [createClient, { loading: loadingCreateClient }] = useMutation<
    createClient,
    createClientVariables
  >(CREATE_CLIENT, {
    update: (cache, mutationResult) => {
      const newClient = mutationResult.data?.createClient;
      const data: any = cache.readQuery({
        query: FILTERED_CLIENTS_QUERY,
        variables: clientFilterData?.queryVars,
      });
      if (data) {
        const clientNameSearch = clientFilterData?.queryVars!.filter!.search![0] || "";
        const clientInSearch = clientPresentInSearch(clientNameSearch, newClient);
        if (clientInSearch) {
          cache.writeQuery({
            query: FILTERED_CLIENTS_QUERY,
            variables: clientFilterData?.queryVars,
            data: {
              filteredClients: {
                clients: [...data.filteredClients.clients, newClient],
                total: data.filteredClients.total + 1,
              },
            },
          });
        }
      }
    },
  });

  const [updateClient, { loading: loadingUpdateClient }] = useMutation<
    UpdateClientMutation,
    UpdateClientMutationVariables
  >(UPDATE_CLIENT, {
    update: (cache, mutationResult) => {
      const updatedClient = mutationResult.data?.updateClient;
      const data = cache.readQuery({
        query: FILTERED_CLIENTS_QUERY,
        variables: clientFilterData?.queryVars,
      }) || {
        filteredClients: {
          __typename: "ClientResult",
          clients: [],
          total: 0,
        },
      };

      if (data && updatedClient) {
        const updatedClientIndex: number = data.filteredClients.clients.findIndex(
          (c: any) => c.id === updatedClient.id
        );

        const mergedClient = {
          ...data.filteredClients.clients[updatedClientIndex],
          ...updatedClient,
        };

        const updatedClientData = [
          ...data.filteredClients.clients.slice(0, updatedClientIndex),
          mergedClient,
          ...data.filteredClients.clients.slice(updatedClientIndex + 1),
        ];

        cache.writeQuery({
          query: FILTERED_CLIENTS_QUERY,
          variables: clientFilterData?.queryVars,
          data: {
            filteredClients: {
              clients: updatedClientData,
              total: data.filteredClients.total,
            },
          },
        });
      }
    },
  });

  return (
    <ClientSettings
      loading={loading || loadingCreateClient || loadingUpdateClient}
      clientFilterData={clientFilterData}
      onClientSave={
        ((values: any) => {
          createClient({ variables: { data: values as any } });
        }) as OnClientSave
      }
      onUpdateClientExcludeTax={(clientData) => {
        const updateClientData = {
          id: clientData.clientId,
          excludeTax: clientData.excludeTax,
        };
        updateClient({
          variables: {
            id: updateClientData.id,
            data: {
              excludeTax: { set: updateClientData.excludeTax },
              gpmDangerThreshold: {
                set: clientData.gpmDangerThreshold ? Number(clientData.gpmDangerThreshold) : null,
              },
              gpmWarningThreshold: {
                set: clientData.gpmWarningThreshold ? Number(clientData.gpmWarningThreshold) : null,
              },
            },
          },
        });
      }}
    />
  );
};
